import { Component, Element, State, Prop, Listen, h, Event, EventEmitter, Method } from '@stencil/core';
import { breakpoints } from '../../utils/variables';
import { getInternalFormatFromDate } from '../../utils/dates';

const DESKTOP_MONTHS_NB = 2;

@Component({
  tag: 'som-date-range-picker',
  styleUrl: 'som-date-range-picker.scss',
})
export class SomDateRangePicker {
  @Element() el: HTMLElement;

  calendarEl: HTMLSomCalendarElement;

  resizeTimeout;

  updateWidthCb;

  /**
   * This property sets the start date.
   */
  @Prop({ mutable: true, reflect: true }) startDate: string = '';

  /**
   * This property sets the end date.
   */
  @Prop({ mutable: true, reflect: true }) endDate: string = '';

  /**
   * This property represents the minimum date that can be selected.
   */
  @Prop() minDate: string = '';

  /**
   * This property represents the maximum date that can be selected.
   */
  @Prop() maxDate: string = '';

  /**
   * When set to `true`, this property sets the date range picker to read only.
   */
  @Prop() readonly = false;

  /**
   * Switch the date range picker to vertical mode allowing users to navigate months vertically instead of horizontally.
   */
  @Prop() vertical = false;

  @State() monthsNb = DESKTOP_MONTHS_NB;

  /**
   * This event is emitted whenever new dates are selected.
   * @Returns `event.detail` will contain the new start date and end date.  
   * 
   * Start Date : `event.detail.startDate`
   * 
   * End Date : `event.detail.endDate`
   */
  @Event() datesChanged: EventEmitter;

  @Listen('resize', { target: 'window' })
  handleWindowResize() {
    clearTimeout(this.resizeTimeout);

    // Setup debounce so we're not constantly reading screen width
    this.resizeTimeout = setTimeout(this.updateWidthCb, 300);
  }

  /**
   * This method resets the date range picker.
   */
  @Method() async reset() {
    return this.calendarEl.reset();
  }

  /**
   * This method sets the focus onto the date range picker.
   */
  @Method() async setFocus() {
    return this.calendarEl.setFocus();
  }

  updateWidth() {
    this.monthsNb = window.innerWidth < breakpoints.lg ? 1 : DESKTOP_MONTHS_NB;
  }

  componentWillLoad() {
    this.updateWidthCb = this.updateWidth.bind(this);
    this.updateWidth();
  }

  handleDateChange(e: CustomEvent) {
    e.stopPropagation();

    this.startDate = e.detail.date1 ? getInternalFormatFromDate(e.detail.date1) : '';
    this.endDate = e.detail.date2 ? getInternalFormatFromDate(e.detail.date2) : '';

    this.datesChanged.emit({
      startDate: e.detail.date1,
      endDate: e.detail.date2,
    });
  }

  render() {
    return this.vertical ? (
      <som-calendar-vertical
        ref={ref => {
          this.calendarEl = ref;
        }}
        enableRangePicker={true}
        readonly={this.readonly}
        date1={this.startDate}
        date2={this.endDate}
        minDate={this.minDate}
        maxDate={this.maxDate}
        onDateChange={this.handleDateChange.bind(this)}
      />
    ) : (
      <som-calendar
        ref={ref => {
          this.calendarEl = ref;
        }}
        enableRangePicker={true}
        monthsNb={this.monthsNb}
        readonly={this.readonly}
        date1={this.startDate}
        date2={this.endDate}
        minDate={this.minDate}
        maxDate={this.maxDate}
        onDateChange={this.handleDateChange.bind(this)}
      />
    );
  }
}
